import type { CommerceOperation } from "../contracts/commerce-operation.js";
import type { CommerceTransport } from "../contracts/commerce-transport.js";
import type { CommerceTransportContext } from "../contracts/commerce-transport-context.js";
import { CommerceTransportError } from "../errors/commerce-transport-error.js";

export interface MockCommerceTransportOptions {
    /** Artificial execution delay used to exercise loading and cancellation behaviour. */
    readonly latencyMilliseconds?: number;
}

export interface MockCommerceOperationContext extends CommerceTransportContext {
    readonly operationName: string;
}

export type MockCommerceOperationHandler<TRequest, TResponse> = (
    request: TRequest,
    context: MockCommerceOperationContext,
) => TResponse | Promise<TResponse>;

type UnknownMockCommerceOperationHandler = (
    request: unknown,
    context: MockCommerceOperationContext,
) => unknown | Promise<unknown>;

/**
 * Deterministic in-memory transport for SDK development, reference applications,
 * integration tests, and consumer prototypes.
 *
 * It uses the same CommerceTransport contract as future Gateway transports, so
 * replacing mock behaviour does not alter Commerce domain code.
 */
export class MockCommerceTransport implements CommerceTransport {
    public readonly name = "mock";

    private readonly handlers = new Map<string, UnknownMockCommerceOperationHandler>();
    private readonly latencyMilliseconds: number;

    public constructor(options: MockCommerceTransportOptions = {}) {
        this.latencyMilliseconds = resolveLatencyMilliseconds(options.latencyMilliseconds);
    }

    /** Registers one handler and rejects accidental duplicate operation registrations. */
    public register<TRequest, TResponse>(
        operation: CommerceOperation<TRequest, TResponse>,
        handler: MockCommerceOperationHandler<TRequest, TResponse>,
    ): this {
        if (typeof handler !== "function") {
            throw new TypeError(`Mock handler for '${operation.name}' must be a function.`);
        }

        if (this.handlers.has(operation.name)) {
            throw new TypeError(
                `Mock handler for Commerce operation '${operation.name}' is already registered.`,
            );
        }

        this.handlers.set(operation.name, handler as UnknownMockCommerceOperationHandler);

        return this;
    }

    /** Registers a fixed immutable response for an operation. */
    public respond<TRequest, TResponse>(
        operation: CommerceOperation<TRequest, TResponse>,
        response: TResponse,
    ): this {
        return this.register(operation, () => response);
    }

    public async execute<TRequest, TResponse>(
        operation: CommerceOperation<TRequest, TResponse>,
        request: TRequest,
        context: CommerceTransportContext = {},
    ): Promise<TResponse> {
        throwIfCancelled(operation.name, context.signal);

        const handler = this.handlers.get(operation.name);

        if (handler === undefined) {
            throw new CommerceTransportError(
                "operation-unsupported",
                `Mock Commerce operation '${operation.name}' has no registered handler.`,
                { operationName: operation.name },
            );
        }

        await waitForLatency(this.latencyMilliseconds, operation.name, context.signal);
        throwIfCancelled(operation.name, context.signal);

        try {
            const response = await handler(request, {
                ...context,
                operationName: operation.name,
            });

            throwIfCancelled(operation.name, context.signal);

            return response as TResponse;
        } catch (error) {
            if (error instanceof CommerceTransportError) {
                throw error;
            }

            throw new CommerceTransportError(
                "request-failed",
                `Mock Commerce operation '${operation.name}' failed.`,
                {
                    operationName: operation.name,
                    cause: error,
                },
            );
        }
    }
}

function resolveLatencyMilliseconds(value: number | undefined): number {
    if (value === undefined) {
        return 0;
    }

    if (!Number.isFinite(value) || value < 0) {
        throw new TypeError(
            "Mock Commerce transport latencyMilliseconds must be a finite number greater than or equal to zero.",
        );
    }

    return value;
}

function throwIfCancelled(operationName: string, signal: AbortSignal | undefined): void {
    if (signal?.aborted !== true) {
        return;
    }

    throw new CommerceTransportError(
        "request-cancelled",
        `Commerce operation '${operationName}' was cancelled.`,
        { operationName },
    );
}

async function waitForLatency(
    latencyMilliseconds: number,
    operationName: string,
    signal: AbortSignal | undefined,
): Promise<void> {
    if (latencyMilliseconds === 0) {
        return;
    }

    await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
            signal?.removeEventListener("abort", cancel);
            resolve();
        }, latencyMilliseconds);

        const cancel = () => {
            clearTimeout(timeout);
            signal?.removeEventListener("abort", cancel);
            reject(
                new CommerceTransportError(
                    "request-cancelled",
                    `Commerce operation '${operationName}' was cancelled.`,
                    { operationName },
                ),
            );
        };

        signal?.addEventListener("abort", cancel, { once: true });
    });
}
